import React, { useState, useRef, RefObject, DragEvent, useCallback } from 'react';
import { useConversationContext } from '../../../context/ChatContext/contextConvo';
import { usePolyglot } from '../../../context/Polyglot';
import { useTypedDispatch } from '../../../redux/hooks';
import { addOneToast } from '../../../redux/slices';

const MAX_TOTAL_FILE_SIZE = 40 * 1024 * 1024;

interface DragAndDropProps {
    uploadInputRef: RefObject<HTMLInputElement>;
    customClass?: string;
    children?: React.ReactNode;
}

export const DragAndDrop: React.FC<DragAndDropProps> = ({
    uploadInputRef,
    customClass,
    children,
}) => {
    const { conversation, useChatAction, files } = useConversationContext();
    const dispatch = useTypedDispatch();

    // const dadData = useTypedSelector(selectDragAndDropData);

    const disabled = conversation.type === 'integration';

    const [dropzoneActive, setDropzoneActive] = useState<boolean>(false);

    const { t } = usePolyglot();

    const dropRef = useRef<HTMLDivElement>(null);

    let dragCounter = 1;

    const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
        if (disabled) return;

        event.preventDefault();
        event.stopPropagation();
    }, []);

    const handleDragEnter = useCallback((event: DragEvent<HTMLDivElement>) => {
        if (disabled || event.dataTransfer.items.length < 1 || !event.dataTransfer.types.includes('Files')) return;

        event.preventDefault();
        event.stopPropagation();

        dragCounter += 1;

        setDropzoneActive(true);
    }, []);

    const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
        if (disabled) return;

        event.preventDefault();
        event.stopPropagation();

        // eslint-disable-next-line no-plusplus
        dragCounter--;

        if (dragCounter === 0 || !dropRef.current?.contains(event.target as Node)) {
            setDropzoneActive(false);
        }
    }, []);

    const handleDrop = useCallback((event: DragEvent<HTMLDivElement>) => {
        if (disabled) return;

        event.preventDefault();
        event.stopPropagation();

        if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            uploadFiles(event.dataTransfer.files);

            dragCounter = 0;
        }

        setDropzoneActive(false);
    }, []);

    const uploadFiles = useCallback((newFiles: FileList | File[]) => {
        const currentSize = files.reduce((sum, f) => sum + f.size, 0);
        const newSize = Array.from(newFiles).reduce((sum, f) => sum + f.size, 0);

        if (currentSize + newSize > MAX_TOTAL_FILE_SIZE) {
            dispatch(addOneToast({
                content: "Total file size must be under 40 MB",
                title: "Files too large",
                type: "error"
            }));
            return;
        }

        useChatAction({
            type: "add_file_to_upload",
            payload: newFiles
        });
    }, [files]);

    return (
        <div
            ref={dropRef}
            className={`drag-and-drop ${dropzoneActive && 'drag-hover'} ${customClass && customClass
                }`}
            style={{ position: 'relative' }}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            {dropzoneActive && (
                <div
                    style={{
                        backgroundColor: 'transparent',
                        position: 'absolute',
                        top: '0',
                        bottom: '0',
                        left: '0',
                        right: '0',
                        height: '100%',
                        width: '100%',
                        zIndex: 2
                    }}
                >
                    <div className='target'>
                        <div className='drop-modal'>
                            <p>{t("phrases.drop_files_here")}!</p>
                        </div>
                    </div>
                </div>
            )}
            <input
                ref={uploadInputRef}
                type='file'
                onChange={event => event.target.files && uploadFiles(event.target.files)}
                onClick={event => {
                    // eslint-disable-next-line no-param-reassign
                    (event.target as HTMLInputElement).value = '';
                }}
                multiple
                tabIndex={-1}
            />
            {children}
        </div>
    );
};
